import React, { useState, useEffect, useRef } from 'react';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    addMonths,
    isSameMonth,
} from 'date-fns';
import {
    Box,
    Button,
    Typography,
    Grid,
    Paper,
    useMediaQuery,
    useTheme,
    Modal,
    IconButton,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    CardGiftcard as CardGiftcardIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Diamond as DiamondIcon,
    Close as CloseIcon,
    DoneOutline as DoneOutlineIcon,
    CalendarToday as CalendarTodayIcon
} from "@mui/icons-material";
import { lighten, darken } from "@material-ui/core";
import ClaimOverlay from "./ClaimOverlay";
import {useGetDailyMissionCalendarQuery, useClaimMissionMutation, useClaimDailyLoginMissionMutation} from "../features/api/missionApiSlice";
import { useLocation, useNavigate } from "react-router-dom";
import * as Constant from "../features/constant";
import { useTranslation } from "react-i18next";
import {
  useGetProfileQuery,
} from "../features/api/userApiSlice";

import Swal from "sweetalert2";

const LoginReward = ({ loginRewardOpen, setLoginRewardOpen }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [claimedDays, setClaimedDays] = useState([]);
    const [missedDays, setMissedDays] = useState([]);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const theme = useTheme();
    const mobileView = useMediaQuery(theme.breakpoints.down('sm'));
    const loginLight = lighten(theme.palette.button.login, 0.1);
    const loginDark = darken(theme.palette.button.login, 0.05);
    const signupLight = lighten(theme.palette.button.signup, 0.1);
    const signupDark = darken(theme.palette.button.signup, 0.05);
    const calendarRef = useRef(null);
    const modalContentRef = useRef(null); 
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const {
      data: user,
      isLoading: isUserLoading,
      isError: isUserError,
      isSuccess: isUserSuccess,
      error: userError,
    } = useGetProfileQuery();

    const {
        data: dailyMissionCalendar,
        isLoading,
        isError,
        refetch, 
      } = useGetDailyMissionCalendarQuery({
        userId: user?.result?.userId,
        year: parseInt(format(currentMonth, "yyyy")),
        month: parseInt(format(currentMonth, "M")),
      });
      const calendarDays = dailyMissionCalendar?.result?.days || [];
      const statusMap = calendarDays.reduce((acc, day) => {
        acc[day.day] = day;
        return acc;
      }, {});

      
      const [claimOverlayOpen, setClaimOverlayOpen] = useState(false);
const [rewardAmount, setRewardAmount] = useState(null);

// prevent multiple triggers
const [overlayShown, setOverlayShown] = useState(false);

const [claimMission, { isLoading: isClaiming }] = useClaimMissionMutation();
const [claimDailyLoginMission, { isClaimDailyLoginMissionLoading }] = useClaimDailyLoginMissionMutation();


useEffect(() => {
    if (loginRewardOpen) {
      refetch();
    }
  }, [loginRewardOpen, refetch]);
// useEffect(() => {
//   if (
//     loginRewardOpen &&                                   // modal is open
//     dailyMissionCalendar?.result?.showClaimOverlay &&    // API says show
//     !overlayShown                                        // not already shown
//   ) {
//     setClaimOverlayOpen(true);
//     setOverlayShown(true);  // ✅ mark as shown
//   }
// }, [loginRewardOpen, dailyMissionCalendar, overlayShown]);

// keep calendar up-to-date whenever the modal opens; when modal closes reset overlay state
useEffect(() => {
  if (loginRewardOpen) {
    // ensure we have fresh calendar data when opening
    refetch();
    return;
  }

  // modal closed -> clear overlay states so reopening is fresh
  setClaimOverlayOpen(false);
  setOverlayShown(false);
}, [loginRewardOpen, refetch]);

// respond to backend's showClaimOverlay flag (open or close overlay)
// NOTE: only act when the modal is open
useEffect(() => {
  const shouldShow = !!dailyMissionCalendar?.result?.showClaimOverlay;

  if (!loginRewardOpen) {
    // if modal isn't open, don't auto-open overlay — but keep state reset handled above
    return;
  }

  if (shouldShow && !overlayShown) {
    // backend says show overlay and we haven't shown it yet
    setClaimOverlayOpen(true);
    setOverlayShown(true);
    return;
  }

  if (!shouldShow && (overlayShown || claimOverlayOpen)) {
    // backend says no overlay -> ensure we close and reset local guard
    setClaimOverlayOpen(false);
    setOverlayShown(false);
  }
}, [dailyMissionCalendar?.result?.showClaimOverlay, loginRewardOpen, overlayShown, claimOverlayOpen]);



    const handleMissionAction = async () => {
    try {
      const response = await claimDailyLoginMission({
        userId: user?.result?.userId,
      }).unwrap();

      setClaimOverlayOpen(false);
    //   setLoginRewardOpen(false);
  
      console.log("Claim result:", response);
  
      await Swal.fire({
        title: "Claim successful!",
        text: `You have won ${response.result}!`,
        icon: "success",
        target: modalContentRef.current,
      });
    } catch (err) {
      console.error("Failed to claim mission:", err);
  
      await Swal.fire({
        title: "Error",
        text: `Failed to claim. Please try again.`,
        icon: "error",
        target: modalContentRef.current,
      });
    }
  };

    const renderHeader = () => {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Button
              size={mobileView ? 'small' : 'medium'}
              sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}
              onClick={() => setCurrentMonth(prev => addMonths(prev, -1))}
            >
              {'◄'}
            </Button>
            <Typography variant={mobileView ? 'subtitle1' : 'h6'} fontWeight="bold">
              {format(currentMonth, 'MMMM yyyy')}
            </Typography>
            <Button
              size={mobileView ? 'small' : 'medium'}
              sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}
              onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
            >
              {'►'}
            </Button>
          </Box>
        );
      };


    const renderDays = () => {
        const date = new Date();
        return (
            <Grid container spacing={0.5} mb={1}>
                {[...Array(7)].map((_, i) => (
                    <Grid item xs={mobileView ? 1.714 : 1.714} key={i}>
                        <Typography
                            variant="caption"
                            align="center"
                            display="block"
                            fontWeight="bold"
                        >
                            {format(addDays(startOfWeek(date), i), 'EEE')}
                        </Typography>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);
        const rows = [];

        let day = startDate;

        while (day <= endDate) {
            const days = [];

            for (let i = 0; i < 7; i++) {
                const dateStr = format(day, 'yyyy-MM-dd');
                const isCurrentMonth = isSameMonth(day, currentMonth);

                // const isClaimed = claimedDays.includes(dateStr);
                // const isMissed = missedDays.includes(dateStr);
                const dayNum = parseInt(format(day, 'd'), 10);
                const dayInfo = statusMap[dayNum]; // from API
                const status = dayInfo?.status;

                days.push(
                    <Grid item xs={mobileView ? 1.714 : 1.714}  padding="4px 4px 4px 4px" key={dateStr}>
                        {isCurrentMonth ? (
                            <Box>
                                                        <Typography textAlign="center" variant="body2" fontSize={mobileView ? '0.75rem' : '0.875rem'}>
                                                        {dayNum}
                                                      </Typography>
                            <Paper
                            elevation={mobileView ? 1 : 2}
                            sx={{
                              height: mobileView ? 40 : 40,
                              marginBottom: 0.3,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor:
                                status === "COMPLETED_CLAIMED" ? '#73BF76' :
                                status === "MISSED" ? '#D07777' :
                                status === "COMPLETED_UNCLAIMED" ? '#E1C16E':
                                status === "UPCOMING" ? '#6a6a6a' : 'white',
                              border: '1px solid #e0e0e0',
                              p: 0.5,
                            }}
                          >

                            {status === "COMPLETED_UNCLAIMED" && <CardGiftcardIcon/>}
                            {status === "MISSED" && <CancelIcon/>}
                            {status === "UPCOMING" && <DiamondIcon/>}
                            {status === "COMPLETED_CLAIMED" && <CheckCircleIcon/>}
                          </Paper>
                          </Box>
                        ) : (
                            <Box sx={{ height: mobileView ? 48 : 60 }} />
                        )}
                    </Grid>
                );

                day = addDays(day, 1);
            }

            rows.push(
                <Grid container spacing={0.5} mb={0.5} key={day}>
                    {days}
                </Grid>
            );
        }

        return <Box>{rows}</Box>;
    };



//     return (
//         <Modal open={loginRewardOpen} onClose={() => setLoginRewardOpen(false)}>
//             <Box
//                 maxWidth={mobileView ? '95%' : 700}
//                 width={mobileView ? '95%' : '650px'}
//                 mx="auto"
//                 px={mobileView ? 2 : 4}
//                 py={mobileView ? 2 : 3}
//                 borderRadius={3}
//                 // color={process.env.REACT_APP_COLOR_TEXT ? process.env.REACT_APP_COLOR_TEXT : "white"}
//                 // bgcolor="background.paper"
//                 sx={{
//                     background: "linear-gradient(135deg,rgba(34, 34, 34, 0.84) 50%,rgba(107, 107, 107, 0.85) 100%)", // blue gradient
//                     color: "white",
//                     position: "absolute",
//                     top: "50%",
//                     left: "50%",
//                     transform: "translate(-50%, -50%)",
//                     outline: "none",
//                 }}
//             >
//                 <IconButton
//                     aria-label="close"
//                     onClick={() => setLoginRewardOpen(false)}
//                     sx={{
//                         position: "absolute",
//                         top: -40,
//                         right: 0,
//                         zIndex: 2,
//                     }}
//                 >
//                     <CloseIcon />
//                 </IconButton>

//                 {renderHeader()}
//                 <Typography
//                     variant={"subtitle2"}
//                     align="center"
//                     fontWeight="bold"
//                     mb={1}
//                 >
//                     Total Check-In: {dailyMissionCalendar?.result?.totalCheckIns || 0}
//                 </Typography>
//                 {renderDays()}
//                 {renderCells()}
// {/* 
//                 <Box mt={2} textAlign="center">
//                     <Button
//                         fullWidth={mobileView}
//                         variant="contained"
//                         color="secondary"
//                         onClick={() => isLoggedIn && claimReward(new Date())}
//                         size={mobileView ? 'small' : 'medium'}
//                     >
//                         Claim Reward
//                     </Button>
//                 </Box> */}
//                 {mobileView && (
//                     <>
//                 <Box
//                 mt={2}
//                 display="flex"
//                 flexDirection={mobileView ? "column" : "row"}
//                 gap={2}
//                 justifyContent="center"
//                 alignItems="center"
//             >
//                 <Button
//                     sx={{
//                     backgroundImage: `linear-gradient(to bottom, ${signupLight} 0%, ${signupDark} 100%)`,
//                     borderRadius: "20px", // rounded corners
//                     px: 3,                // horizontal padding
//                     minWidth: mobileView ? "90%" : "150px", // less width
//                     }}
//                     variant="contained"
//                     onClick={() => window.location.href = "/mission"}
//                 >
//                     <CalendarTodayIcon/> &nbsp;
//                     DAILY MISSION
//                 </Button>

//                 {/* <Button
//                     sx={{
//                     backgroundImage: `linear-gradient(to bottom, ${loginLight} 0%, ${loginDark} 100%)`,
//                     borderRadius: "20px",
//                     px: 3,
//                     minWidth: mobileView ? "90%" : "150px",
//                     }}
//                     variant="contained"
//                     onClick={() => window.location.href = "/home"}
//                 >
//                     Home
//                 </Button> */}
//             </Box>
//             </>
//             )}
//             </Box>
//         </Modal>
//     );
    if (isLoading || isError || !dailyMissionCalendar?.result?.days?.length) {
        return null;
    }

    return (
        <>
        <Modal open={loginRewardOpen} onClose={() => setLoginRewardOpen(false)}>
            
          <Box
            ref={modalContentRef}
            maxWidth={mobileView ? "95%" : 800}
            width={mobileView ? "95%" : "750px"}
            maxHeight="90vh"                  // 👈 cap modal height to viewport
            overflow="auto"   
            mx="auto"
            borderRadius={3}
            sx={{
              background: "linear-gradient(135deg,rgba(34, 34, 34, 0.84) 50%,rgba(107, 107, 107, 0.85) 100%)",
              color:process.env.REACT_APP_COLOR_TEXT ? process.env.REACT_APP_COLOR_TEXT : "white",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              outline: "none",
              display: "flex",
              flexDirection: mobileView ? "column" : "row", // <-- split desktop vs mobile
              overflow: "visible",
            }}
          >
            {/* Left Section (Desktop only) */}
            {!mobileView && (
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                borderRadius={3}
                p={3}
                sx={{
                    background: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)", // warm gradient
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 60, mb: 2, color: "white" }} />
                <Button
                  variant="contained"
                  sx={{
                    backgroundImage: `linear-gradient(to bottom, ${signupLight} 0%, ${signupDark} 100%)`,
                    borderRadius: "30px",
                    px: 4,
                    py: 1,
                    fontWeight: "bold",
                    color: "white",
                    boxShadow: 3,
                  }}
                  // onClick={() => (window.location.href = "/profileWallet")}
                  onClick={() => {
                    const isAlreadyInMissionDiary =
                      location.pathname === Constant.getPath("/profileWallet") &&
                      location.state?.screen === "mission diary";
                
                    if (isAlreadyInMissionDiary) {
                      setLoginRewardOpen(false);
                    } else {
                      navigate(Constant.getPath("/profileWallet"), {
                        state: { screen: "mission diary" },
                      });
                      setLoginRewardOpen(false); 
                    }
                  }}

                >
                  {t('mission.missiondiary').toUpperCase()}
                </Button>
              </Box>
            )}
      
            {/* Right Section (Calendar) */}
            <Box
            ref={calendarRef}   // 👈 attach ref here
            flex={2}
            position="relative" // 👈 important so overlay is scoped inside
            px={mobileView ? 2 : 4}
            py={mobileView ? 2 : 3}
          >
              <IconButton
                aria-label="close"
                onClick={() => setLoginRewardOpen(false)}
                sx={{
                  position: "absolute",
                  top: -40,
                  right: 0,
                  zIndex: 9999,
                }}
              >
                <CloseIcon />
              </IconButton>
      
              {renderHeader()}
              <Typography
                variant={"subtitle2"}
                align="center"
                fontWeight="bold"
                mb={1}
              >
                {t('mission.totalcheckin')}: {dailyMissionCalendar?.result?.totalCheckIns || 0}
              </Typography>
      
              {renderDays()}
              {renderCells()}
      
              {/* Mobile-only button */}
              {mobileView && (
                <Box
                  mt={2}
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Button
                    sx={{
                      backgroundImage: `linear-gradient(to bottom, ${signupLight} 0%, ${signupDark} 100%)`,
                      borderRadius: "20px",
                      px: 3,
                      minWidth: "90%",
                      fontWeight: "bold",
                    }}
                    variant="contained"
                    onClick={() => {
                      const isAlreadyInMissionDiary =
                        location.pathname === Constant.getPath("/profileWallet") &&
                        location.state?.screen === "mission diary";
                  
                      if (isAlreadyInMissionDiary) {
                        setLoginRewardOpen(false);
                      } else {
                        navigate(Constant.getPath("/profileWallet"), {
                          state: { screen: "mission diary" },
                        });
                        setLoginRewardOpen(false); 
                      }
                    }}
                  >
                    <CalendarTodayIcon /> &nbsp; {t('mission.missiondiary').toUpperCase()}
                  </Button>
                </Box>
              )}

<ClaimOverlay
    open={claimOverlayOpen}
    rewardAmount={rewardAmount}
    onClose={() => setClaimOverlayOpen(false)}
    containerRef={calendarRef} 
    onClaim={handleMissionAction}
    isClaiming={isClaiming} 
  />
            </Box>
          </Box>
        </Modal>

        {/* <ClaimOverlay
  open={claimOverlayOpen}
  rewardAmount={rewardAmount}
  onClose={() => setClaimOverlayOpen(false)}
/> */}
</>
      );
};

export default LoginReward;
